import { ReactElement } from 'react';
import { Typography, List, Spin } from 'antd';
import {
  TranscriptionBlock,
  TranscriptRow,
  Timestamp,
  Speaker,
  TranscriptCard,
  CenteredMessage,
  SpinnerMargin,
  StyledListItem,
} from './Transcription.style';
import DownloadButton from './DownloadButton';
import downloadTextFile from '../../utils/downloadTextFile';
import { formatSpeaker } from './formatSpeaker';
import { formatFileNamePart } from './formatFileNamePart';

const { Text } = Typography;

const RECORDING_STATUS = {
  PROCESSING: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const;

interface FullTextSegment {
  caption: string;
  speaker: string;
  time_code: string;
}

interface Props {
  fullText: FullTextSegment[] | null;
  recordingStatusId: number;
  title: string;
  advisorName: string | null;
  customerName: string | null;
  advisorPhone: string | null;
  customerPhone: string | null;
}

const Transcription = ({
  fullText,
  recordingStatusId,
  title,
  advisorName,
  customerName,
  advisorPhone,
  customerPhone,
}: Props): ReactElement => {
  const handleDownload = (): void => {
    if (fullText === null) {
      return;
    }
    const content = fullText
      .map(s => `${s.time_code}\t${formatSpeaker(s.speaker, advisorName, customerName)}\t${s.caption}`)
      .join('\n');
    const advisorPart = formatFileNamePart(advisorName, advisorPhone);
    const customerPart = formatFileNamePart(customerName, customerPhone);
    const fileName = `${title}-${advisorPart}-${customerPart}.txt`;
    downloadTextFile(content, fileName);
  };

  const isDownloadable = recordingStatusId === RECORDING_STATUS.COMPLETED && fullText !== null;

  const renderContent = (): ReactElement => {
    if (recordingStatusId === RECORDING_STATUS.PROCESSING) {
      return (
        <CenteredMessage>
          <Spin />
          <SpinnerMargin>
            <Text type='secondary'>文字起こしを処理中です</Text>
          </SpinnerMargin>
        </CenteredMessage>
      );
    }

    if (recordingStatusId === RECORDING_STATUS.FAILED) {
      return (
        <CenteredMessage>
          <Text type='danger'>処理に失敗しました</Text>
        </CenteredMessage>
      );
    }

    if (fullText === null) {
      return (
        <CenteredMessage>
          <Text type='secondary'>文字起こしデータがありません</Text>
        </CenteredMessage>
      );
    }

    return (
      <List
        dataSource={fullText}
        renderItem={segment => (
          <StyledListItem>
            <TranscriptRow>
              <Timestamp>{segment.time_code}</Timestamp>
              <Speaker>{formatSpeaker(segment.speaker, advisorName, customerName)}</Speaker>
              <Text>{segment.caption}</Text>
            </TranscriptRow>
          </StyledListItem>
        )}
      />
    );
  };

  return (
    <TranscriptionBlock>
      <TranscriptCard
        title='完全な文字起こし'
        size='small'
        extra={isDownloadable ? <DownloadButton onClick={handleDownload} /> : null}
      >
        {renderContent()}
      </TranscriptCard>
    </TranscriptionBlock>
  );
};

export default Transcription;
